
import { defineComponent, PropType } from 'vue';
import {
  NAMED_ROUTES,
  BOOKING_STATUSES,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
  APP_HERO_PRODUCTS,
} from '@white-label-configuration/constants';
import { datadogRum } from '@datadog/browser-rum';
import { useScreenSize } from '@white-label-helper/shared-composables';

// Types
import type { Policy, ManageBookingParsedOrderItem } from '@white-label-types/parking-booking';
import type { Partners } from '@white-label-types/partners';
import type VueI18n from 'vue-i18n/types';

// Store
import { mapState } from 'pinia';
import { useManageBookingStore } from '@white-label-store/manage-booking';
import type { ManageBookingParsedState } from '@white-label-types/stores';
import {
  goToApp,
  getDomainUrl,
  ECOMMERCE_URLS,
  DOMAINS_NAMES,
} from '@white-label-helper/switch-app';

// Helpers
import { checkIsLessOneHour } from '@white-label-helper/time-helpers';
import { guessTimezone } from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { usePartnerConfigHelper } from '@white-label-helper/shared-helpers';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

// Components
import BookingStatus from '../booking-status/booking-status.vue';
import BTicketAdditionalInfo from '../b-ticket-additional-info/b-ticket-additional-info.vue';
import BTicket from '../b-ticket/b-ticket.vue';
import ManageBookingShareButton from '../manage-booking-share-button/manage-booking-share-button.vue';
import Tooltip from '../tooltip/tooltip.vue';
import LoadingScreen from '../loading-screen/loading-screen.vue';
import ManageBookingBundleContainer from '../manage-booking-bundle-container/manage-booking-bundle-container.vue';

export default defineComponent({
  name: 'ManageBookingOrderDetails',

  components: {
    BookingStatus,
    BTicketAdditionalInfo,
    BTicket,
    ManageBookingShareButton,
    ManageBookingBundleContainer,
    Tooltip,
    LoadingScreen,
    IconButton: () => import('@white-label-icon/icon-button'),
  },

  props: {
    orderData: {
      type: Object as PropType<ManageBookingParsedState>,
      required: true,
    },

    modifications: {
      type: Object,
      default: () => {},
    },
  },

  setup() {
    const { isMobileResolution } = useScreenSize();
    return { isMobileResolution };
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState(useManageBookingStore,
      [
        'readBookingStatus',
        'readIsConfirmed',
        'readIsInProgress',
        'readIsCancelled',
        'readIsPastBooking',
        'readProductTypesCategories',
      ]
    ),
    hideDirection(): boolean {
      return this.modifications?.hideDirections
    },
    showMultipleOrderItems(): boolean {
      return this.orderData.items.length > 1;
    },
    disabledExtendTooltipText(): VueI18n.TranslateResult {
      const isExtendableExtranet = (this.orderData.inventory_item?.is_extendable ?? false);
      if (!isExtendableExtranet) {
        return this.$t('UI.tooltip.notExtendableExtranet');
      }
        return this.$t('UI.tooltip.notExtendable');
    },
    shouldShowAmendCancelButtons() {
      return (
        usePartnerConfigHelper().isCrossSellFlowEnabled ||
        this.showMultipleOrderItems ||
        this.parkingExtensionEnabled
      );
    },
    parkingExtensionEnabled() {
      return getFeatureFlag(FEATURE_FLAGS.PT_2973_PARKING_BOOKING_EXTENSION) === true
    },

    /**
     * Sort order items by entry date and time ascending.
     * If entry time is not provided, sort at the end, alphabetically by product code.
     */
    sortedOrderItems(): ManageBookingParsedState['items'] {
      const items = [...this.orderData.items.filter((item) => !item.bundle_id)];
      return items.sort((a, b) => {
        const productCodeA = a.product_code;
        const productCodeB = b.product_code;

        const aIsExtrasOrInsurance = productCodeA === PRODUCT_TYPES.EXTRAS || productCodeA === PRODUCT_TYPES.TRAVEL_INSURANCE;
        const bIsExtrasOrInsurance = productCodeB === PRODUCT_TYPES.EXTRAS || productCodeB === PRODUCT_TYPES.TRAVEL_INSURANCE;

        if (aIsExtrasOrInsurance && !bIsExtrasOrInsurance) {
          return 1;
        }
        if (!aIsExtrasOrInsurance && bIsExtrasOrInsurance) {
          return -1;
        }

        const entryTimeA = a.search_criteria[productCodeA]?.time1;
        const entryTimeB = b.search_criteria[productCodeB]?.time1;

        const entryDateTimeA = a.details?.entry_date;
        const entryDateTimeB = b.details?.entry_date;

        const nameA = a.inventory_item?.display_name;
        const nameB = b.inventory_item?.display_name;

        if (!entryTimeA && !entryTimeB) {
          return productCodeA.localeCompare(productCodeB);
        } else if (!entryTimeA) {
          return 1;
        } else if (!entryTimeB) {
          return -1;
        } else if (entryDateTimeA === entryDateTimeB) {
          return nameA.localeCompare(nameB);
        } else {
          return (
            new Date(entryDateTimeA).getTime() -
            new Date(entryDateTimeB).getTime()
          );
        }
      });
    },

    getBookingStatus(): ReturnType<typeof readBookingStatus> {
      return this.readBookingStatus;
    },

    isConfirmed(): ReturnType<typeof readIsConfirmed> {
      return this.readIsConfirmed;
    },

    isInProgress(): ReturnType<typeof readIsInProgress> {
      return this.readIsInProgress;
    },

    isCancelled(): ReturnType<typeof readIsCancelled> {
      return this.readIsCancelled;
    },

    isPastBooking(): ReturnType<typeof readIsPastBooking> {
      return this.readIsPastBooking;
    },

    cancellationPolicies(): Policy[] {
      if (this.orderData.cancellationProtection) {
        return this.orderData.cancellationProtection.cancellation_policies;
      }
      return this.orderData.cancellationPolicies || [];
    },

    subItems(): { title: string }[] {
      if (this.orderData.groups) {
        return this.orderData.groups.map((title: string) => ({ title }));
      }
      return [];
    },

    entryDateTime(): string {
      return this.orderData.entryDateTime || '';
    },
    exitDateTime(): string {
      return this.orderData.exitDateTime || '';
    },
    partnerTimeZone(): Partners['poi']['timezone'] {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    showBundleItems(): boolean {
      return !!this.orderData.bundle && !!this.orderData.bundle.items.length;
    },

    isBundleCancellable(): boolean {
      return this.orderData.bundle?.items.every((item) => item.is_cancellable)
        && this.orderData.bundle?.items.some((item) => {
            return !checkIsLessOneHour(item.entryExitInfo.stringEntryTime, this.partnerTimeZone);
        });
    },
  },

  methods: {
    itemIsParkingProduct(
      item: ManageBookingParsedOrderItem
    ): boolean {
      return item.product_code === 'parking';
    },
    isTravelInsuranceProduct(
      item: ManageBookingParsedOrderItem
    ): boolean {
      return item.product_code === APP_HERO_PRODUCTS.TRAVEL_INSURANCE;
    },
    itemIsLessOneHour(item: ManageBookingParsedOrderItem): boolean {
      return checkIsLessOneHour(item.entryExitInfo?.stringEntryTime, this.partnerTimeZone);
    },
    isProductTypeIncluded(key: string): boolean {
      return this.readProductTypesCategories?.includes(key);
    },

    isItemExtended(item: ManageBookingParsedOrderItem): boolean {
    const isExtendableExtranet = item?.inventory_item?.is_extendable ?? false;
    if (!isExtendableExtranet) {
      return true;
    }
    return this.orderData.items.some(i => i.upgraded_from_order_item_id === item.id);
    },
    cancelBookingHandler(item: ManageBookingParsedOrderItem) {
      if (item.product_code === APP_HERO_PRODUCTS.TRAVEL_INSURANCE) {
        this.loading = true;

        setTimeout(() => {
          this.loading = false;
          this.$router.push({
            path: NAMED_ROUTES.manage_booking.cancel,
            query: { cancelItemId: item.id.toString() },
          });
        }, 1800);
        return;
      }

      this.$router.push({
        path: NAMED_ROUTES.manage_booking.cancel,
        query: { cancelItemId: item.id.toString() },
      });
    },
    cancelBundleHandler() {
      this.$router.push({
        path: NAMED_ROUTES.manage_booking.cancel,
        query: { cancelBundleId: this.orderData.bundle.id },
      });
    },
    amendBookingHandler(item: ManageBookingParsedOrderItem) {
      goToApp(
        getDomainUrl(DOMAINS_NAMES.ECOMMERCE),
        ECOMMERCE_URLS.MODIFY,
        `amendItemId=${item.id}`
      );
    },
    extendBookingHandler(item: ManageBookingParsedOrderItem) {
      goToApp(
        getDomainUrl(DOMAINS_NAMES.ECOMMERCE),
        ECOMMERCE_URLS.MODIFY,
        `extendItemId=${item.id}`
      );
    },
    itemIsPrivateTerminalProduct(
      item: ManageBookingParsedOrderItem
    ) {
      return item.inventory_item?.product_option?.code === 'lounge_private';
    },
    isOrderItemCancelled(item: ManageBookingParsedOrderItem) {
      return item.status.toUpperCase() === BOOKING_STATUSES.CANCELLED;
    },
    toggleDetailsFn(isOpen: boolean, item: ManageBookingParsedOrderItem) {
      if (item.product_code === PRODUCT_TYPES.FASTTRACK) {
        datadogRum.addAction('disclosureCard', {
          value: isOpen ? 'expanded' : 'collapsed'
        });
      }
    },
    resolveBookingStatus(item: ManageBookingParsedOrderItem) {
      return item?.upgraded_from_order_item_id != null && item?.status === 'confirmed'
        ? 'EXTENDED'
        : item?.status?.toUpperCase() || '';
    },
    shouldShowExtendButton(item?: ManageBookingParsedOrderItem): boolean {
      const statusCheck = item ? item.status === 'in_progress' : this.isInProgress;
      const isParking = item ? item.product_code === 'parking' : this.itemIsParkingProduct;
      const isUpgraded = !!item?.upgraded_from_order_item_id;

      return this.parkingExtensionEnabled && statusCheck && isParking && !isUpgraded;
    },

    // Hide button for all past booking
    // Hide button for parking items when it is in progress
    shouldShowAmend(item: ManageBookingParsedOrderItem): boolean {
      if (this.itemIsParkingProduct(item)) {
        return item.status !== 'in_progress'
          && item.status !== 'past_booking'
          && !this.orderData.items.some(i => i.upgraded_from_order_item_id === item.id)
          && item?.upgraded_from_order_item_id == null;
      }
      return item.status !== 'past_booking';
    }
  },
});
